import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';

export default function DashboardPage() {
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => apiClient.getCurrentUser(),
  });

  const { data: decks } = useQuery({
    queryKey: ['decks'],
    queryFn: () => apiClient.getDecks(),
  });

  const { data: matches } = useQuery({
    queryKey: ['matches'],
    queryFn: () => apiClient.getMatches(),
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-responsive-xl font-bold text-white">Dashboard</h1>
        <p className="mt-2 text-zinc-400 text-responsive">
          Welcome back, <span className="text-cyan-400 font-semibold">{user?.username}</span>! Here's what's happening in your card games.
        </p>
      </div>

      <div className="mobile-grid">
        {/* Quick Stats */}
        <div className="mobile-card card-hover">
          <h3 className="text-responsive-lg font-medium text-white">Your Decks</h3>
          <p className="mt-2 text-2xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-orange-400 bg-clip-text text-transparent">
            {decks?.length || 0}
          </p>
          <p className="mt-1 text-sm text-zinc-400">
            Custom card decks created
          </p>
        </div>

        <div className="mobile-card card-hover">
          <h3 className="text-responsive-lg font-medium text-white">Active Matches</h3>
          <p className="mt-2 text-2xl sm:text-3xl font-bold text-green-400">
            {matches?.filter(m => m.status === 'IN_PROGRESS').length || 0}
          </p>
          <p className="mt-1 text-sm text-zinc-400">
            Games currently in progress
          </p>
        </div>

        <div className="mobile-card card-hover">
          <h3 className="text-responsive-lg font-medium text-white">Available Matches</h3>
          <p className="mt-2 text-2xl sm:text-3xl font-bold text-cyan-400">
            {matches?.filter(m => m.status === 'WAITING').length || 0}
          </p>
          <p className="mt-1 text-sm text-zinc-400">
            Matches waiting for players
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <div className="mobile-card">
          <h3 className="text-responsive-lg font-medium text-white">Quick Actions</h3>
          <div className="mt-4 space-y-3">
            <Link
              to="/decks"
              className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/10 touch-target"
            >
              <div>
                <p className="font-medium text-white">Build a Deck</p>
                <p className="text-sm text-zinc-400">Create or edit your card decks</p>
              </div>
              <svg className="h-5 w-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            <Link
              to="/lobby"
              className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/10 touch-target"
            >
              <div>
                <p className="font-medium text-white">Join a Match</p>
                <p className="text-sm text-zinc-400">Find and join available games</p>
              </div>
              <svg className="h-5 w-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        <div className="mobile-card">
          <h3 className="text-responsive-lg font-medium text-white">Recent Activity</h3>
          <div className="mt-4">
            {matches && matches.length > 0 ? (
              <div className="space-y-3">
                {matches.slice(0, 3).map((match) => (
                  <div key={match.id} className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{match.name}</p>
                      <p className="text-sm text-zinc-400">
                        {match.currentPlayers}/{match.maxPlayers} players • {match.status}
                      </p>
                    </div>
                    <Link
                      to={`/game/${match.id}`}
                      className="text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors touch-target flex-shrink-0 ml-2"
                    >
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-sm">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
